import { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Textarea,
  mergeClasses,
} from '@coreai/fluentui-react';
import { SendRegular, EditRegular } from '@fluentui/react-icons';
import type { ClarificationSnapshot } from '@/types/agui';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  cardAnswered: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  question: {
    color: tokens.colorNeutralForeground1,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  optionButton: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    whiteSpace: 'normal',
    height: 'auto',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  optionButtonAnswered: {
    cursor: 'default',
    opacity: 0.6,
  },
  optionButtonPicked: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    opacity: 1,
  },
  somethingElseRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  somethingElseTextarea: {
    flexGrow: 1,
    minWidth: 0,
  },
  answeredHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

interface ClarificationCardProps {
  snapshot: ClarificationSnapshot;
  /**
   * Whether the card can accept input. When false (history-rehydrated or
   * post-answer), buttons and the text input are disabled; the picked
   * option (if any) is highlighted.
   */
  interactive: boolean;
  /**
   * Called when the user picks an option or sends a custom answer.
   * `optionId` is omitted for custom-text answers.
   */
  onAnswer?: (text: string, optionId?: string) => void;
}

/**
 * Renders a clarification request as a chat card. Two visual modes:
 *
 * - **Options mode** (`snapshot.options.length > 0`): stacked buttons, one
 *   per option, plus a "Something else" button that expands a text input.
 * - **Free-text mode** (`snapshot.options.length === 0`): just the text input
 *   and a send button.
 *
 * In both modes, when `interactive === false` the controls are disabled and
 * the picked option (if any) is highlighted so the historical record reads
 * as "the question was asked and answered like this".
 */
export const ClarificationCard = ({ snapshot, interactive, onAnswer }: ClarificationCardProps) => {
  const styles = useStyles();
  const hasOptions = snapshot.options.length > 0;
  const answered = !!snapshot.answer;
  const [customExpanded, setCustomExpanded] = useState(!hasOptions);
  const [customText, setCustomText] = useState('');

  const handlePick = useCallback((label: string, sendValue: string | undefined, optionId: string) => {
    if (!interactive || !onAnswer) return;
    onAnswer((sendValue ?? label).trim(), optionId);
  }, [interactive, onAnswer]);

  const handleSendCustom = useCallback(() => {
    if (!interactive || !onAnswer) return;
    const text = customText.trim();
    if (!text) return;
    onAnswer(text, undefined);
    setCustomText('');
  }, [interactive, onAnswer, customText]);

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter inserts newline (consistent with ChatInput).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendCustom();
    }
  };

  return (
    <div className={mergeClasses(styles.card, answered && styles.cardAnswered)} role="group" aria-label="Clarification request">
      <Text className={styles.question} weight="semibold" size={300}>
        {snapshot.question}
      </Text>

      {hasOptions && (
        <div className={styles.options}>
          {snapshot.options.map((opt) => {
            const isPicked = answered && snapshot.answer?.optionId === opt.id;
            return (
              <Button
                key={opt.id}
                appearance={isPicked ? 'primary' : 'outline'}
                className={mergeClasses(
                  styles.optionButton,
                  !interactive && styles.optionButtonAnswered,
                  isPicked && styles.optionButtonPicked,
                )}
                disabled={!interactive}
                onClick={() => handlePick(opt.label, opt.sendValue, opt.id)}
              >
                {opt.label}
              </Button>
            );
          })}

          {snapshot.allowCustom && interactive && !customExpanded && (
            <Button
              appearance="subtle"
              icon={<EditRegular />}
              className={styles.optionButton}
              onClick={() => setCustomExpanded(true)}
            >
              Something else…
            </Button>
          )}
        </div>
      )}

      {interactive && customExpanded && (
        <div className={styles.somethingElseRow}>
          <Textarea
            className={styles.somethingElseTextarea}
            value={customText}
            onChange={(_e, data) => setCustomText(data.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder={hasOptions ? 'Tell me what you\'d like to do…' : 'Type your answer…'}
            aria-label={hasOptions ? 'Something else — type your own answer' : 'Type your answer'}
            rows={2}
            resize="vertical"
          />
          <Button
            appearance="primary"
            icon={<SendRegular />}
            disabled={!customText.trim()}
            onClick={handleSendCustom}
            aria-label="Send answer"
          />
        </div>
      )}

      {answered && (
        <Text size={200} className={styles.answeredHint}>
          {snapshot.answer?.optionId
            ? `Answered: ${snapshot.options.find((o) => o.id === snapshot.answer?.optionId)?.label ?? snapshot.answer.sentText}`
            : `Answered: ${snapshot.answer?.sentText}`}
        </Text>
      )}
    </div>
  );
};
